import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, Typography } from '@mui/material'
import { shareTeamContent } from 'api/teams'
import NotificationSys from 'components/NotificationSystem'
import Loader from 'components/placeholders/ModalLoadingPlaceholder'
import { ShareFields } from 'features/Share/components/ShareFields'
import { useShareRecipients } from 'features/Share/hooks/useShareRecipients'
import { shareFormSchema, ShareFormValues, ShareType } from 'features/Share/models/shareForm'
import { minButtonWidth } from 'const'
import React from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { ArticleType, TeamShareRequest } from 'types'

export function ArticleShareForm({
  onClose,
  entityId,
  entityType,
}: {
  onClose: () => void
  entityId: number
  entityType: ArticleType | 'note'
}) {
  const { teamMembersOptions, isTeamMembersLoading, activeTeamId, activeTeamName, resolveUserIds } =
    useShareRecipients()

  const methods = useForm<ShareFormValues>({
    mode: 'onChange',
    resolver: zodResolver(shareFormSchema),
    defaultValues: {
      type: ShareType.GROUP,
      members: [],
      comments: '',
    },
  })

  const { handleSubmit } = methods

  const shareMutation = useMutation(shareTeamContent, {
    onSuccess: () => {
      onClose()
      NotificationSys.showSuccess(`Content was successfully shared`)
    },
  })

  const onSubmitHandler: SubmitHandler<ShareFormValues> = (values: ShareFormValues) => {
    if (!activeTeamId) {
      NotificationSys.showWarning('Please select a team to share the content')
      return
    }

    const request: TeamShareRequest = {
      userIds: resolveUserIds(values.type, values.members),
      careTeamId: activeTeamId,
      comment: values.comments || '',
      entityId,
      type: entityType,
    }
    shareMutation.mutate(request)
  }

  return (
    <Stack gap={2}>
      <Typography variant="body1">
        You can share this content with the entire group {activeTeamName || ''} or specific members
      </Typography>

      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
          <Loader show={isTeamMembersLoading} />

          {!isTeamMembersLoading && (
            <Stack spacing={1.5}>
              <ShareFields options={teamMembersOptions} />

              <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
                  Close
                </Button>
                <LoadingButton
                  loading={false}
                  variant="contained"
                  type="submit"
                  sx={minButtonWidth}
                >
                  Save
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </Box>
      </FormProvider>
    </Stack>
  )
}
